import type { AudienceGated } from "@/client/core/Core.api";
import { isAppVisible, type AudienceState } from "@/client/core/services/audience";
import type { IconName } from "@/client/ui/design-system/icons";
import { kMaxFetchSpliceSamplesUuids } from "@/shared/fetch-splice-samples.api";

/**
 * The retrieval capabilities the Sounds dock offers, as modes of one search
 * field. Each is gated like an app: `audiences` and an optional flag decide
 * who sees it, through the same `isAppVisible` the home page uses for its
 * cards, so promoting a mode from R&D to internal is the same edit as
 * promoting an app. A mode is not an app, though: it has no page of its own,
 * and it lives on every website page inside the dock.
 */
export type SoundsBrowserModeId = "describe" | "coso" | "siso" | "presets" | "fetch";

export type SoundsBrowserModeInput =
    /** The query drives the search; filters refine it. */
    | "text"
    /** A seed sample drives the search; the query and filters refine it. */
    | "audio"
    /** A seed sample alone drives the search. */
    | "audio-only"
    /** The field takes catalog ids rather than words. */
    | "uuids";

export interface SoundsBrowserMode extends AudienceGated {
    readonly id: SoundsBrowserModeId;
    /** In the scope menu and the title row. */
    readonly label: string;
    readonly icon: IconName;
    /**
     * What the field asks for while empty. Shown for the text modes only:
     * while a seeded mode is active the field stays Search's, so a seeded
     * mode's placeholder is never on screen.
     */
    readonly placeholder: string;
    /** One line under the label in the scope menu. */
    readonly description: string;
    readonly input: SoundsBrowserModeInput;
    /** Whether the Atlantis filter row applies. */
    readonly usesFilters: boolean;
    /** Whether results come in pages the list can keep asking for. */
    readonly isPaged: boolean;
    readonly emptyTitle: string;
    /** Absent for the home state, which shows the recommendations under the field instead of an instruction. */
    readonly emptyBody?: string;
}

export const kDefaultSoundsBrowserModeId: SoundsBrowserModeId = "describe";

export const kSoundsBrowserModes: readonly SoundsBrowserMode[] = [
    {
        id: "describe",
        label: "Search",
        icon: "search",
        placeholder: "Describe the sound you want",
        description: "Search the catalog by words and filters.",
        input: "text",
        usesFilters: true,
        isPaged: true,
        // No instruction: the home state shows the recommendations under the
        // field instead, and the list stays empty until something is searched.
        emptyTitle: "Search Splice Sounds",
        // The dock's baseline: everyone who can see the dock can search it.
        audiences: ["rnd", "internal", "soundcheck", "partner"],
    },
    {
        // Atlantis SiSo: the nearest sounds to the sample by audio alone. First
        // of the seeded modes, so it is where a seed lands by default.
        id: "siso",
        label: "Similar",
        icon: "siso",
        placeholder: "Describe the sound you want",
        description: "The nearest sounds to a sample, by audio alone.",
        input: "audio-only",
        usesFilters: false,
        isPaged: false,
        emptyTitle: "Find similar sounds",
        emptyBody: "Drop a sample above, or use the sparkle on any result.",
        audiences: ["rnd"],
    },
    {
        // Atlantis CoSo: sounds that go with the sample. Splice's own
        // "Complementary sounds".
        id: "coso",
        label: "Complementary",
        icon: "searchSparkles",
        placeholder: "Describe the sound you want",
        description: "Sounds that go with a sample, narrowed by the filters.",
        input: "audio",
        usesFilters: true,
        isPaged: true,
        emptyTitle: "Find complementary sounds",
        emptyBody: "Drop a sample above, or use the sparkle on any result.",
        audiences: ["rnd"],
    },
    {
        // Modal preset search: presets for Splice's instruments that sound
        // like the sample. Rows carry a preview and little else; see
        // `preset-results.ts`.
        id: "presets",
        label: "Presets",
        icon: "preset",
        placeholder: "Describe the sound you want",
        description: "Presets that sound like a sample, for Splice's instruments.",
        input: "audio-only",
        usesFilters: false,
        isPaged: false,
        emptyTitle: "Find presets",
        emptyBody: "Drop a sample above, or use the sparkle on any result.",
        audiences: ["rnd"],
    },
    {
        id: "fetch",
        label: "Fetch",
        icon: "link",
        placeholder: "Paste Splice sample UUIDs",
        description: "Look up samples directly by catalog id.",
        input: "uuids",
        usesFilters: false,
        isPaged: false,
        emptyTitle: "Fetch by UUID",
        emptyBody: `Paste one or more Splice sample UUIDs, up to ${kMaxFetchSpliceSamplesUuids} at a time.`,
        audiences: ["rnd"],
    },
];

const kModesById = new Map(kSoundsBrowserModes.map((mode) => [mode.id, mode]));

export const isSoundsBrowserModeId = (value: unknown): value is SoundsBrowserModeId =>
    typeof value === "string" && kModesById.has(value as SoundsBrowserModeId);

export const getSoundsBrowserMode = (id: SoundsBrowserModeId): SoundsBrowserMode => {
    const mode = kModesById.get(id);
    if (mode === undefined) {
        throw new Error(`Unknown sounds browser mode: ${id}`);
    }
    return mode;
};

/** The modes this visitor can use, in menu order. */
export const getVisibleSoundsBrowserModes = (
    audienceState: Readonly<AudienceState>,
): readonly SoundsBrowserMode[] =>
    kSoundsBrowserModes.filter((mode) => isAppVisible(mode, audienceState));

/** A mode that searches from a sample rather than from words. */
export const isSeededMode = (mode: SoundsBrowserMode): boolean =>
    mode.input === "audio" || mode.input === "audio-only";

/**
 * The seeded modes are chosen in the sound-search zone under the field, not
 * in the field's own scope menu, which lists only what the words do.
 */
export const getSeededModes = (modes: readonly SoundsBrowserMode[]): SoundsBrowserMode[] =>
    modes.filter(isSeededMode);

export const getTextModes = (modes: readonly SoundsBrowserMode[]): SoundsBrowserMode[] =>
    modes.filter((mode) => !isSeededMode(mode));

/** Whether any mode that takes a seed sample is on offer, which is what shows the sparkle on rows and the zone. */
export const hasSeededMode = (modes: readonly SoundsBrowserMode[]): boolean =>
    modes.some(isSeededMode);

const takesAudio = (mode: SoundsBrowserMode): boolean =>
    mode.input === "audio" || mode.input === "audio-only";

/**
 * Which mode a seed lands in. An explicit ask wins when that mode is on offer
 * (a row's sparkle asks for Similar); otherwise a mode already taking audio
 * keeps the seed, and a text mode hands it to the first seeded mode on offer,
 * in the order the zone's tabs show them. Undefined when nothing visible takes
 * audio.
 */
export const pickSeededMode = (
    modes: readonly SoundsBrowserMode[],
    options?: {
        readonly currentModeId?: SoundsBrowserModeId;
        readonly preferredModeId?: SoundsBrowserModeId;
    },
): SoundsBrowserModeId | undefined => {
    const audioModes = modes.filter(takesAudio);
    const preferred = audioModes.find((mode) => mode.id === options?.preferredModeId);
    if (preferred !== undefined) {
        return preferred.id;
    }
    const current = audioModes.find((mode) => mode.id === options?.currentModeId);
    if (current !== undefined) {
        return current.id;
    }
    return audioModes[0]?.id;
};

/**
 * Splice sample UUIDs out of whatever was pasted: one per line, comma
 * separated, or copied out of a URL. Anything shorter than a UUID fragment
 * is noise. Order kept, duplicates dropped, capped at what the route takes.
 */
export const parseSampleUuids = (raw: string): string[] => {
    const seen = new Set<string>();
    const uuids: string[] = [];
    for (const token of raw.split(/[\s,;]+/)) {
        const candidate = token.trim().replace(/^.*\//, "");
        if (candidate.length < 8 || !/^[A-Za-z0-9-]+$/.test(candidate) || seen.has(candidate)) {
            continue;
        }
        seen.add(candidate);
        uuids.push(candidate);
        if (uuids.length >= kMaxFetchSpliceSamplesUuids) {
            break;
        }
    }
    return uuids;
};
